import { Inject, Injectable, signal } from "@angular/core";
import { LvfAuthenticationStatus } from "@kiraah-shared/common";
import type {
  IMfaVerificationRequest,
  ISelectTenantRequest,
  ITenantSelectionDataInfo,
} from "@kiraah-shared/common";
import { MOBILE_AUTH_SERVICE, IMobileAuthService } from "./mobile-auth.service.interface";
import { isMobileAccessTokenExpiredOrNearExpiry } from "./mobile-access-token.util";
import { MobileAppCacheService } from "../cache/mobile-app-cache.service";
import { TOKEN_STORAGE, ITokenStorage } from "../http/token-storage.interface";
import {
  MobileAuthResult,
  MobileLoginRequest,
  OtpLoginRequest,
  OtpLoginRequestResult,
  OtpLoginVerifyRequest,
} from "../models/mobile-auth.models";
import { MobileProfileService } from "../profile/services/mobile-profile.service";
import { MobileAuthStore } from "../state/mobile-auth.store";
import { MobileProfileStore } from "../state/mobile-profile.store";

@Injectable({ providedIn: "root" })
export class MobileSessionService {
  private refreshPromise: Promise<void> | null = null;
  private sessionBootstrapInProgress = false;
  private bootstrapCompleted = false;

  private readonly pendingTenantSelectionSignal = signal<ITenantSelectionDataInfo | null>(null);
  /** Set when login/MFA returns TENANT_SELECTION_REQUIRED; consumed by tenant-selection screen. */
  readonly pendingTenantSelection = this.pendingTenantSelectionSignal.asReadonly();

  constructor(
    @Inject(MOBILE_AUTH_SERVICE) private readonly authService: IMobileAuthService,
    @Inject(TOKEN_STORAGE) private readonly tokenStorage: ITokenStorage,
    private readonly cacheService: MobileAppCacheService,
    private readonly authStore: MobileAuthStore,
    private readonly profileStore: MobileProfileStore,
    private readonly profileService: MobileProfileService
  ) {}

  /** Pendant le bootstrap F5 : l'intercepteur ne déconnecte pas si le refresh échoue. */
  isSessionBootstrapInProgress(): boolean {
    return this.sessionBootstrapInProgress;
  }

  /**
   * Bootstrap mobile adapté de `app-initializer.ts` (web).
   * Cache local puis `/me` avant le 1er rendu → 1 seul F5 suffit pour les droits à jour.
   */
  async bootstrapSession(): Promise<void> {
    if (this.bootstrapCompleted) {
      return;
    }

    const [token, cachedResult] = await Promise.all([
      this.tokenStorage.get(),
      this.cacheService.readAuthResult(),
    ]);

    if (!token) {
      this.authStore.setUnauthenticated();
      await this.profileService.tryRestoreFromCache();
      this.bootstrapCompleted = true;
      return;
    }

    const cachedSnapshot = cachedResult;
    await this.restoreSessionFromCache(token, cachedSnapshot);

    this.sessionBootstrapInProgress = true;
    try {
      const currentToken = (await this.tokenStorage.get()) ?? token;
      if (isMobileAccessTokenExpiredOrNearExpiry(currentToken)) {
        await this.tryRefreshTokenSilently();
      }
      await this.profileService.refreshSessionFromBackend();
    } catch {
      // Conserver la session restaurée depuis le cache.
    } finally {
      this.sessionBootstrapInProgress = false;
    }

    if (!this.authStore.isAuthenticated()) {
      await this.restoreSessionFromCache(token, cachedSnapshot);
    }

    this.bootstrapCompleted = true;
  }

  private async restoreSessionFromCache(
    token: string,
    cachedResult: MobileAuthResult | null
  ): Promise<void> {
    if (cachedResult?.ProfilData) {
      const tokens = cachedResult.Tokens ?? {
        AccessToken: token,
        RefreshToken: "",
        ExpiresInSeconds: 3600,
      };
      this.authStore.setSession({
        ...cachedResult,
        Tokens: { ...tokens, AccessToken: token },
      });
      this.profileService.hydrateFromAuthState();
      return;
    }

    const profile = await this.cacheService.readProfile();
    if (!profile) {
      return;
    }

    this.authStore.setSession({
      Status: LvfAuthenticationStatus.SUCCESS,
      Tokens: {
        AccessToken: token,
        RefreshToken: "",
        ExpiresInSeconds: 3600,
      },
      ProfilData: profile as MobileAuthResult["ProfilData"],
    });
    this.profileService.hydrateFromAuthState();
  }

  private async tryRefreshTokenSilently(): Promise<void> {
    try {
      await this.refreshTokenIfNeeded();
    } catch {
      // On tentera /me avec le token en cache.
    }
  }

  /**
   * Password login. Persists session only when Status is SUCCESS with access token.
   * Caller must handle MFA_REQUIRED and TENANT_SELECTION_REQUIRED.
   */
  async login(data: MobileLoginRequest): Promise<MobileAuthResult> {
    this.authStore.setLoading();
    try {
      const result = await this.authService.login(data);
      if (
        result.Status === LvfAuthenticationStatus.SUCCESS &&
        result.Tokens?.AccessToken
      ) {
        this.pendingTenantSelectionSignal.set(null);
        await this.persistSession(result);
      } else if (
        result.Status === LvfAuthenticationStatus.TENANT_SELECTION_REQUIRED &&
        result.TenantSelection
      ) {
        this.pendingTenantSelectionSignal.set(result.TenantSelection);
        this.authStore.resetAfterLoginAttempt();
      } else {
        this.authStore.resetAfterLoginAttempt();
      }
      return result;
    } catch (error) {
      this.authStore.resetAfterLoginAttempt();
      throw error;
    }
  }

  async verifyMfa(data: IMfaVerificationRequest): Promise<MobileAuthResult> {
    this.authStore.setLoading();
    try {
      const result = await this.authService.verifyMfa(data);
      if (
        result.Status === LvfAuthenticationStatus.SUCCESS &&
        result.Tokens?.AccessToken
      ) {
        this.pendingTenantSelectionSignal.set(null);
        await this.persistSession(result);
      } else if (
        result.Status === LvfAuthenticationStatus.TENANT_SELECTION_REQUIRED &&
        result.TenantSelection
      ) {
        this.pendingTenantSelectionSignal.set(result.TenantSelection);
        this.authStore.resetAfterLoginAttempt();
      } else {
        this.authStore.resetAfterLoginAttempt();
      }
      return result;
    } catch (error) {
      this.authStore.resetAfterLoginAttempt();
      throw error;
    }
  }

  async selectTenant(data: ISelectTenantRequest): Promise<MobileAuthResult> {
    this.authStore.setLoading();
    try {
      const result = await this.authService.selectTenant(data);
      if (
        result.Status === LvfAuthenticationStatus.SUCCESS &&
        result.Tokens?.AccessToken
      ) {
        this.pendingTenantSelectionSignal.set(null);
        await this.persistSession(result);
      } else {
        this.authStore.resetAfterLoginAttempt();
      }
      return result;
    } catch (error) {
      this.authStore.resetAfterLoginAttempt();
      throw error;
    }
  }

  async requestOtpLogin(data: OtpLoginRequest): Promise<OtpLoginRequestResult> {
    return this.authService.requestOtpLogin(data);
  }

  async verifyOtpLogin(data: OtpLoginVerifyRequest): Promise<MobileAuthResult> {
    this.authStore.setLoading();
    try {
      const result = await this.authService.verifyOtpLogin(data);
      if (
        result.Status === LvfAuthenticationStatus.SUCCESS &&
        result.Tokens?.AccessToken
      ) {
        this.pendingTenantSelectionSignal.set(null);
        await this.persistSession(result);
      } else if (
        result.Status === LvfAuthenticationStatus.TENANT_SELECTION_REQUIRED &&
        result.TenantSelection
      ) {
        this.pendingTenantSelectionSignal.set(result.TenantSelection);
        this.authStore.resetAfterLoginAttempt();
      } else {
        this.authStore.resetAfterLoginAttempt();
        throw new Error("Authentication failed");
      }
      return result;
    } catch (error) {
      this.authStore.resetAfterLoginAttempt();
      throw error;
    }
  }

  async refreshTokenIfNeeded(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      const tokens = await this.authService.refreshToken();
      this.authStore.updateTokens(tokens);
      await this.tokenStorage.set(tokens.AccessToken);
      const authResult = this.authStore.authResult();
      if (authResult) {
        await this.cacheService.saveAuthResult(authResult).catch(() => undefined);
      }
    })();

    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  setPendingTenantSelection(data: ITenantSelectionDataInfo | null): void {
    this.pendingTenantSelectionSignal.set(data);
  }

  async logout(): Promise<void> {
    const authResult = this.authStore.authResult();
    const identityUid = authResult?.ProfilData?.Identity?.Uid ?? "";
    const refreshToken = authResult?.Tokens?.RefreshToken;

    this.authStore.setUnauthenticated();
    this.profileStore.clear();
    this.pendingTenantSelectionSignal.set(null);
    this.bootstrapCompleted = false;

    await Promise.allSettled([
      this.cacheService.clearAll(),
      this.tokenStorage.clear(),
      identityUid
        ? this.authService.logout(String(identityUid), { RefreshToken: refreshToken, RevokeAll: false })
        : Promise.resolve(),
    ]);
  }

  private async persistSession(result: MobileAuthResult): Promise<void> {
    this.authStore.setSession(result);
    this.profileService.hydrateFromAuthState();
    await Promise.all([
      this.tokenStorage.set(result.Tokens!.AccessToken),
      this.cacheService.saveAuthResult(result),
    ]);
  }
}
